import confetti from 'canvas-confetti'
import { Sparkles, Trophy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { EVENT, GRAND_DRAW_NO, RULES } from '@sdg/shared'
import { cn } from '../lib/cn'
import { useScreenStream } from '../lib/useScreenStream'
import WorldRing from '../components/WorldRing'
import type { AwardView, ScreenState } from './admin/screenTypes'
import { DrawScreen } from './screen/DrawScreen'
import { ActiveSchoolScreen } from './screen/ActiveSchoolScreen'
import { ClubScreen } from './screen/ClubScreen'
import { SoundToggle } from './screen/SoundToggle'
import { AttireScreen } from './screen/AttireScreen'
import { PodiumScreen } from './screen/PodiumScreen'
import { ChequeScreen } from './screen/ChequeScreen'

/**
 * The big screen — slides 15 to 17 and 26.
 *
 * A projector in the hall, driven entirely by Admin pressing buttons in the
 * console. It has no controls of its own: it listens on SSE and shows
 * whatever is currently happening, which is either an open lucky draw or an
 * award being revealed.
 *
 * Built for a room, not a desk: everything is sized off `vmin` so the type
 * scales with the projector rather than being fixed in pixels. The idle view
 * uses the event's WorldRing artwork; draw and podium views keep their dark
 * high-contrast stage palette.
 */

export default function Screen() {
  return (
    <>
      <ScreenStages />
      <SoundToggle />
    </>
  )
}

function ScreenStages() {
  /*
   * No sign-in. The screen is public and read-only: whoever sets up the hall
   * plugs a laptop into the projector and opens this URL. The server decides
   * what it is allowed to say — winners yes, other entrants only by first
   * name, unrevealed award places not at all.
   */
  const live = useScreenStream<ScreenState>()
  const state = live.data

  /*
   * What the screen shows.
   *
   * Still derived by default, so Admin's job is one button per action rather
   * than also choosing a mode — but an explicit focus overrides it, because
   * there was previously no way to put the welcome backdrop back once
   * anything had been drawn.
   *
   * The fallbacks order by **timestamp**, not by position in the list. They
   * used to `.reverse().find(...)`, which takes the highest-numbered item
   * rather than the most recent one: finishing Lucky Draw 1 made the screen
   * jump to the Grand Draw whenever the Grand Draw had a winner from a
   * rehearsal.
   */
  const focus = state?.focus
  const draws = state?.draws.draws ?? []
  const awards = state?.ceremony.awards ?? []

  const openDraw = draws.find((d) => d.status === 'open')

  const revealingAward = awards.find((a) => a.published && a.revealedCount > 0 && !a.complete)

  /** Most recently *revealed* completed award, by its latest reveal time. */
  const completedAward = awards
    .filter((a) => a.complete)
    .map((a) => ({
      award: a,
      at: a.results.reduce<string>((latest, r) => (r.revealedAt && r.revealedAt > latest ? r.revealedAt : latest), ''),
    }))
    .sort((x, y) => y.at.localeCompare(x.at))[0]?.award

  /** Most recent draw that has actually produced a winner. */
  const lastDraw = draws
    .filter((d) => d.winners.length > 0)
    .sort((x, y) => (y.lastActivityAt ?? '').localeCompare(x.lastActivityAt ?? ''))[0]

  /** Whatever the Committee pinned, when they pinned something. */
  const pinnedDraw = focus?.mode === 'draw' ? draws.find((d) => d.drawNo === focus.drawNo) : undefined
  const pinnedAward =
    focus?.mode === 'award' ? awards.find((a) => a.code === focus.awardCode) : undefined

  if (!state) {
    return (
      <Stage surface="open" scene="connecting">
        <IdleStage connection={live.state === 'error' ? 'error' : 'connecting'} />
      </Stage>
    )
  }

  /*
   * An explicit focus beats everything. `idle` is checked first because it is
   * the Committee saying "put the backdrop up" — and it has to win even while
   * a draw is open, which is exactly the case that had no answer before.
   */
  if (focus?.mode === 'idle') {
    return (
      <Stage surface="open" scene="idle">
        <IdleStage connection="ready" />
      </Stage>
    )
  }

  /*
   * The presentation cheque. Checked beside `idle` rather than further down,
   * because like `idle` it is the Committee saying what the projector shows —
   * it must win over whichever draw or award the data would otherwise pick.
   */
  if (focus?.mode === 'cheque') {
    return (
      <Stage theme="gold" surface="full" scene="cheque">
        <ChequeScreen draw={draws.find((d) => d.drawNo === GRAND_DRAW_NO)} />
      </Stage>
    )
  }

  if (pinnedDraw) {
    return (
      <Stage theme={pinnedDraw.theme} surface="full" scene={`draw-${pinnedDraw.drawNo}`}>
        <DrawScreen draw={pinnedDraw} pool={state.draws} />
      </Stage>
    )
  }

  if (pinnedAward) {
    return (
      <AwardStage award={pinnedAward} />
    )
  }

  if (openDraw) {
    return (
      <Stage theme={openDraw.theme} surface="full" scene={`draw-${openDraw.drawNo}`}>
        <DrawScreen draw={openDraw} pool={state.draws} />
      </Stage>
    )
  }

  if (revealingAward) {
    return (
      <AwardStage award={revealingAward} />
    )
  }

  if (completedAward) {
    return (
      <AwardStage award={completedAward} />
    )
  }

  if (lastDraw) {
    return (
      <Stage theme={lastDraw.theme} surface="full" scene={`draw-${lastDraw.drawNo}`}>
        <DrawScreen draw={lastDraw} pool={state.draws} />
      </Stage>
    )
  }

  return (
    <Stage surface="open" scene="idle">
      <IdleStage connection="ready" />
    </Stage>
  )
}

/**
 * Best Traditional Attire has its own approved full-bleed layout (slide 25's
 * two portrait cards); the judged awards share the podium frame.
 */
function AwardStage({ award }: { award: AwardView }) {
  // `surface="full"` for both: each award screen draws its own frame through
  // `StageChrome`, so the glass surface's header would be a second one.
  if (award.screen === 'twin') {
    return (
      <Stage theme="purple" surface="full" scene={`award-${award.code}`}>
        <AttireScreen award={award} />
      </Stage>
    )
  }

  // Most Active School: one winner and a counted figure, so the number rolls
  // up rather than a card opening on a podium.
  if (award.screen === 'active') {
    return (
      <Stage theme="gold" surface="full" scene={`award-${award.code}`}>
        <ActiveSchoolScreen award={award} />
      </Stage>
    )
  }

  // Most Active Club: a crest that opens, then the name and its citation.
  if (award.screen === 'club') {
    return (
      <Stage theme="purple" surface="full" scene={`award-${award.code}`}>
        <ClubScreen award={award} />
      </Stage>
    )
  }

  return (
    <Stage theme="gold" surface="full" scene={`award-${award.code}`}>
      <PodiumScreen award={award} />
    </Stage>
  )
}

function IdleStage({ connection }: { connection: 'connecting' | 'error' | 'ready' }) {
  const waiting = connection === 'ready'

  return (
    <div className="grid h-full w-full grid-cols-1 items-center gap-5 px-[2vmin] sm:grid-cols-[1.08fr_0.92fr] sm:gap-[4vmin] sm:px-[3vmin]">
      <section className="max-w-[76vmin]">
        {/*
          The host institution, above the title.

          The welcome backdrop is what the hall looks at for most of the
          morning, so it is the screen the logo matters most on — and the one
          that had none.
        */}
        <img
          src="/lincoln-logo.webp"
          alt="Lincoln University College"
          className="mb-[2.4vmin] h-[7vmin] w-auto rounded-[0.6vmin]"
          draggable={false}
        />
        <p className="text-[2.2vmin] font-bold uppercase tracking-[0.18em] text-blue-700">
          Campus showcase · 15 October 2026
        </p>
        <h1 className="mt-[2vmin] text-[6.8vmin] font-extrabold leading-[1.06] text-[#0B1E3F]">
          {EVENT.name}
        </h1>
        <p className="mt-[1.6vmin] text-[3.7vmin] font-bold italic text-orange-600">
          “{EVENT.tagline}”
        </p>
        <p className="mt-[2.2vmin] max-w-[72vmin] text-[2.4vmin] leading-relaxed text-[#415873]">
          A celebration of sustainable ideas, cultural exchange and student achievement at Lincoln
          University College Malaysia.
        </p>

        <div className="mt-[2.4vmin] flex flex-wrap gap-[1vmin]">
          <StageChip>{EVENT.dateLabel.replace(/^\w+,\s*/, '')}</StageChip>
          <StageChip>{EVENT.countries} countries</StageChip>
          <StageChip>{RULES.totalBooths} school booths</StageChip>
          <StageChip>Hosted in Malaysia</StageChip>
        </div>

        <div className="mt-[3vmin] border-l-[0.35vmin] border-[#0F8B8D] pl-[1.5vmin]">
          <p className="text-[1.5vmin] font-bold uppercase tracking-[0.18em] text-slate-500">
            Stage programme
          </p>
          <p className="mt-[0.5vmin] text-[2.3vmin] font-semibold text-[#183653]">
            {connection === 'error'
              ? 'Reconnecting to the event system'
              : waiting
                ? 'The programme will begin shortly'
                : 'Connecting to the event system'}
          </p>
          <p className="mt-[0.5vmin] text-[1.9vmin] text-slate-500">
            Draws and award reveals will appear here.
          </p>
        </div>
      </section>

      <WorldRing className="mx-auto w-[58vmin] max-w-full drop-shadow-[0_16px_36px_rgba(32,74,125,0.14)]" />
    </div>
  )
}

function StageChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-white/90 bg-white/75 px-[1.9vmin] py-[1vmin] text-[1.8vmin] font-semibold text-[#173B66] shadow-[0_3px_10px_rgba(30,64,110,0.08)] backdrop-blur">
      {children}
    </span>
  )
}

/** Full-bleed event stage, themed per draw — slide 16's "own colour theme". */
function Stage({
  theme = 'navy',
  surface = 'glass',
  scene,
  children,
}: {
  theme?: 'navy' | 'purple' | 'gold'
  surface?: 'glass' | 'open' | 'full'
  /**
   * What is currently on screen — the backdrop, a particular draw, a
   * particular award. Used as a React key so the stage fades in whenever the
   * Committee moves to something else, and *not* on every SSE payload for the
   * thing already showing.
   */
  scene?: string
  children: React.ReactNode
}) {
  const grounds = {
    navy: 'from-navy-950 via-navy-900 to-navy-800',
    purple: 'from-purple-deep via-purple to-navy-900',
    gold: 'from-navy-950 via-navy-900 to-[#3a2a06]',
  } as const
  const idleGrounds = {
    navy: 'from-[#BFD8FA] via-[#E9F1FD] to-[#F5EFFC]',
    purple: 'from-[#DCE6FA] via-[#EBE9F8] to-[#F4EFFA]',
    gold: 'from-[#C8DFF5] via-[#E9F1FD] to-[#F8F1E5]',
  } as const
  const accents = {
    navy: 'from-[#386EAC] via-[#58A7C5] to-[#E6A23C]',
    purple: 'from-[#697CB5] via-[#9380B5] to-[#D8A14C]',
    gold: 'from-[#3C98A5] via-[#6A9FBD] to-[#E6A23C]',
  } as const

  return (
    <div
      className={cn(
        'screen-stage relative isolate flex h-dvh w-full flex-col overflow-hidden',
        surface === 'full'
          ? cn('bg-gradient-to-br', grounds[theme])
          : cn('bg-gradient-to-br p-[3vmin]', surface === 'open' ? idleGrounds[theme] : grounds[theme]),
      )}
    >
      <div className="stage-accent-track absolute inset-x-0 top-0 z-20 h-[0.35vmin] overflow-hidden" aria-hidden>
        <div className={cn('h-full w-full bg-gradient-to-r', accents[theme])} />
        <div className="stage-accent-sweep absolute inset-y-0 left-0 w-[18vmin] bg-gradient-to-r from-transparent via-white/75 to-transparent" />
      </div>

      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
        <Sparkles className="stage-sparkle absolute left-[8%] top-[22%] size-[2vmin] text-[#D6A13C]/75" />
        <Sparkles className="stage-sparkle stage-sparkle-delayed absolute left-[43%] top-[13%] size-[1.2vmin] text-[#65B7CB]/70" />
        <Sparkles className="stage-sparkle stage-sparkle-delayed-2 absolute bottom-[19%] right-[7%] size-[1.7vmin] text-[#D6A13C]/65" />
        <Sparkles className="stage-sparkle absolute bottom-[15%] left-[23%] size-[1.1vmin] text-[#65B7CB]/70" />
      </div>

      {/* Event strip, so a photo of the screen carries the event's name. */}
      {surface !== 'full' && <header className={cn(
        'relative z-10 flex shrink-0 items-center justify-between gap-[2vmin] border-b pb-[1.5vmin]',
        surface === 'open' ? 'border-white/80' : 'border-white/15',
      )}>
        <div className="flex items-center gap-[1.5vmin]">
          <img src="/icon.svg" alt="" className="size-[4vmin]" />
          <div>
            <p className={cn(
              'text-[1.9vmin] font-semibold uppercase tracking-[0.14em]',
              surface === 'open' ? 'text-[#173B66]' : 'text-white/80',
            )}>
              {EVENT.host} · SDG &amp; Cultural Committee
            </p>
            <p className={cn('text-[1.55vmin]', surface === 'open' ? 'text-slate-600' : 'text-white/50')}>
              {EVENT.tagline} · {EVENT.dateLabel.replace(/^\w+,\s*/, '')}
            </p>
          </div>
        </div>
      </header>}

      {surface === 'glass' && (
        <WorldRing className="pointer-events-none absolute -right-[12vmin] top-1/2 z-0 w-[76vmin] -translate-y-1/2 opacity-[0.16]" />
      )}

      <main
        className={cn(
          'relative z-10 flex min-h-0 flex-1',
          surface === 'full' ? 'w-full items-stretch' : 'items-center justify-center',
          surface === 'glass' && 'my-[1.5vmin] px-[1vmin]',
        )}
      >
        <div key={scene} className="stage-enter flex min-h-0 w-full flex-1 items-stretch justify-center">
          {children}
        </div>
      </main>

      {surface !== 'full' && <footer className={cn(
        'relative z-10 flex shrink-0 items-center justify-between gap-4 border-t pt-[1vmin] text-[1.5vmin]',
        surface === 'open' ? 'border-white/70 text-slate-500' : 'border-white/15 text-white/40',
      )}>
        <span>{EVENT.name}</span>
        <span>
          Technology partner ·{' '}
          <b className={cn('font-semibold', surface === 'open' ? 'text-[#173B66]' : 'text-white/65')}>
            {EVENT.technologyPartner}
          </b>
        </span>
      </footer>}
    </div>
  )
}

/**
 * Fires the deck's confetti. Exported so both screens use one implementation
 * and the colours stay the event's.
 */
export function celebrate(theme: 'navy' | 'purple' | 'gold' = 'gold') {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  // Mutable arrays: canvas-confetti's `colors` is typed `string[]`.
  const palettes: Record<typeof theme, string[]> = {
    navy: ['#FCA311', '#2E9E5B', '#0F8B8D', '#FFFFFF', '#5360A8'],
    purple: ['#5360A8', '#FCA311', '#FFFFFF', '#0F8B8D', '#E4572E'],
    gold: ['#FCA311', '#F5B82E', '#FFFFFF', '#C98A0B', '#2E9E5B'],
  }

  const colors = palettes[theme]

  // Two cannons from the lower corners, then a wide overhead fall — this is a
  // hall, so it has to read from thirty metres away.
  confetti({ particleCount: 90, spread: 70, startVelocity: 65, angle: 60, origin: { x: 0, y: 0.9 }, colors, zIndex: 90 })
  confetti({ particleCount: 90, spread: 70, startVelocity: 65, angle: 120, origin: { x: 1, y: 0.9 }, colors, zIndex: 90 })

  window.setTimeout(() => {
    confetti({
      particleCount: 140,
      spread: 130,
      startVelocity: 40,
      decay: 0.92,
      gravity: 0.85,
      ticks: 320,
      origin: { x: 0.5, y: 0.1 },
      colors,
      zIndex: 90,
    })
  }, 260)
}

/** A medal disc, used for both draw winners and podium places. */
export function Medal({
  n,
  tone,
  size = '10vmin',
}: {
  n: number | string
  tone: 'gold' | 'silver' | 'bronze' | 'navy' | 'purple'
  size?: string
}) {
  const tones = {
    gold: 'from-amber to-[#c98a0b] text-navy-950',
    silver: 'from-[#d5dbe6] to-silver text-navy-950',
    bronze: 'from-[#e0a94a] to-bronze text-navy-950',
    navy: 'from-navy-400 to-navy-600 text-white',
    purple: 'from-purple to-purple-deep text-white',
  } as const

  return (
    <span
      style={{ width: size, height: size, fontSize: `calc(${size} * 0.46)` }}
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-linear-to-br font-bold shadow-lift ring-[0.4vmin] ring-white/25',
        tones[tone],
      )}
    >
      {n}
    </span>
  )
}

export { Sparkles, Trophy }

/** Shared by the two screens for the "?" placeholders on slide 15. */
export function useLatest<T>(value: T) {
  const ref = useRef(value)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref
}

/**
 * Cycles names for the slide-17 drum-roll.
 *
 * Returns a `tick` as well as the name, and that is the point: with a pool of
 * one the name cannot change, so the visual has to animate off the tick
 * instead. The previous version did `(i + 1) % names.length` and advanced
 * nothing at all when the length was 1 — on a small rehearsal pool the
 * spotlight simply froze, which read as a broken draw.
 *
 * The order is reshuffled on each spin so a two-name pool does not read as an
 * obvious A-B-A-B flip.
 */
export function useShuffle(names: string[], active: boolean, intervalMs = 90) {
  const [tick, setTick] = useState(0)
  const order = useRef<string[]>([])

  // A fresh order per spin. Fisher-Yates, seeded per spin rather than per
  // render so the sequence does not jump about mid-animation.
  useEffect(() => {
    if (!active) return

    const shuffled = [...names]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    order.current = shuffled
    setTick(0)
  }, [active, names.length])

  useEffect(() => {
    // Ticks even for a single name: the caller animates off this.
    if (!active) return
    const timer = setInterval(() => setTick((t) => t + 1), intervalMs)
    return () => clearInterval(timer)
  }, [active, intervalMs])

  const pool = order.current.length > 0 ? order.current : names
  const name = pool.length > 0 ? pool[tick % pool.length] : ''

  return { name, tick, poolSize: pool.length }
}
